// =============================================================================
// MeuDriver — Batch U4: Gerenciamento de templates de resposta rápida
// Rota: /instrutor/chat-templates
// =============================================================================

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  GripVertical,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import {
  useInstructorChatTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
} from '@/hooks/useChatTemplates'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { InstructorChatTemplateRow } from '@/types/database'

const MAX_TEMPLATES = 20
const MAX_BODY_LENGTH = 500

// ---------------------------------------------------------------------------
// Form de criação / edição
// ---------------------------------------------------------------------------

function TemplateForm({
  initial,
  onSave,
  onCancel,
  isPending,
}: {
  initial?: Pick<InstructorChatTemplateRow, 'label' | 'body'>
  onSave: (values: { label: string; body: string }) => void
  onCancel: () => void
  isPending: boolean
}) {
  const [label, setLabel] = useState(initial?.label ?? '')
  const [body, setBody] = useState(initial?.body ?? '')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!label.trim() || !body.trim()) return
    onSave({ label: label.trim(), body: body.trim() })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-brand-200 bg-brand-50/30 dark:bg-brand-950/20 dark:border-brand-800 p-4">
      <div className="space-y-1">
        <Label className="text-xs font-semibold">Nome do template</Label>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Ex: Confirmar aula"
          maxLength={100}
          required
          data-testid="template-form-label"
        />
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold">Texto da resposta</Label>
          <span className="text-[10px] text-muted-foreground">{body.length}/{MAX_BODY_LENGTH}</span>
        </div>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Texto que será preenchido no chat ao selecionar este template..."
          maxLength={MAX_BODY_LENGTH}
          rows={3}
          required
          data-testid="template-form-body"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-3.5 w-3.5 mr-1" />
          Cancelar
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={isPending || !label.trim() || !body.trim()}
          data-testid="template-form-save"
        >
          <Save className="h-3.5 w-3.5 mr-1" />
          {isPending ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Item de template
// ---------------------------------------------------------------------------

function TemplateItem({
  template,
  instructorId,
}: {
  template: InstructorChatTemplateRow
  instructorId: string
}) {
  const [editing, setEditing] = useState(false)
  const updateTemplate = useUpdateTemplate(instructorId)
  const deleteTemplate = useDeleteTemplate(instructorId)

  if (editing) {
    return (
      <TemplateForm
        initial={{ label: template.label, body: template.body }}
        onSave={({ label, body }) => {
          updateTemplate.mutate(
            { id: template.id, label, body },
            { onSuccess: () => setEditing(false) },
          )
        }}
        onCancel={() => setEditing(false)}
        isPending={updateTemplate.isPending}
      />
    )
  }

  return (
    <div
      className="flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 p-3"
      data-testid={`template-item-${template.id}`}
    >
      <GripVertical className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0 mt-0.5 cursor-grab" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{template.label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{template.body}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-gray-400 hover:text-brand-600"
          onClick={() => setEditing(true)}
          data-testid={`template-edit-${template.id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-gray-400 hover:text-red-600"
          onClick={() => deleteTemplate.mutate(template.id)}
          disabled={deleteTemplate.isPending}
          data-testid={`template-delete-${template.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function InstructorChatSettings() {
  const { user } = useAuth()
  const [creating, setCreating] = useState(false)

  // Resolve o instructor.id
  const { data: instructorRow } = useQuery<{ id: string } | null>({
    queryKey: ['instructor-row-id', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from('instructors')
        .select('id')
        .eq('profile_id', user!.id)
        .maybeSingle()
      return data ?? null
    },
    staleTime: 1000 * 60 * 10,
  })

  const instructorId = instructorRow?.id ?? ''

  const { data: templates = [], isLoading } = useInstructorChatTemplates(instructorId)
  const createTemplate = useCreateTemplate(instructorId)

  const atLimit = templates.length >= MAX_TEMPLATES

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Respostas rápidas
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Templates de texto que você pode usar com um clique durante o chat com alunos.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-brand-600" />
            Meus templates
            <span className="text-xs font-normal text-muted-foreground">
              ({templates.length}/{MAX_TEMPLATES})
            </span>
          </CardTitle>
          {!atLimit && (
            <Button
              size="sm"
              onClick={() => setCreating(true)}
              disabled={creating || !instructorId}
              data-testid="btn-new-template"
              className="gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              Novo template
            </Button>
          )}
        </CardHeader>

        <CardContent className="space-y-3">
          {creating && instructorId && (
            <TemplateForm
              onSave={({ label, body }) => {
                createTemplate.mutate(
                  { label, body, display_order: templates.length },
                  { onSuccess: () => setCreating(false) },
                )
              }}
              onCancel={() => setCreating(false)}
              isPending={createTemplate.isPending}
            />
          )}

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : templates.length === 0 && !creating ? (
            <div className="rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 py-10 text-center">
              <FileText className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Nenhum template ainda
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Crie até {MAX_TEMPLATES} templates de resposta rápida.
              </p>
            </div>
          ) : (
            <div className={cn('space-y-2', !instructorId && 'pointer-events-none opacity-60')}>
              {templates.map((t) => (
                <TemplateItem
                  key={t.id}
                  template={t}
                  instructorId={instructorId}
                />
              ))}
            </div>
          )}

          {atLimit && (
            <p className="text-xs text-amber-600 text-center pt-1">
              Limite de {MAX_TEMPLATES} templates atingido. Remova um para criar outro.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
